import { Middleware } from 'swr';
import { useEffect, useRef } from 'react';
import { GenericV1ErrorsArray } from 'src/types/v1-api-types/_global';
import { useStaticToasts } from 'src/hooks/useStaticToasts/useStaticToasts';
import { SwrToastsProps } from '@type/swr';


/**
 * @description SWR middleware: show toasts on data change. It will show only for props that are in the config object.
 * * **showSuccessToast** - boolean - show success toast on data change; default undefined
 * * **showErrorToast** - boolean - show error toast on data change; default undefined
 * * **successToastText** - string - custom success toast text; default undefined. If not provided, it will show a default success toast.
 * * **errorToastText** - string - custom error toast text; default undefined. If not provided, it will show a generic error toast.
 * * **useGenericErrorToast** - boolean - use generic error toast with the default text; default undefined.
 * * **byErrorCodes** - ErrorByCode[] - show error toast based on error codes; default undefined.
 */
export const withToasts = (props: SwrToastsProps): Middleware => (useSWRNext) => (key, fetcher, config) => {
  // Destructure props
  const {
    showSuccessToast,
    showErrorToast,
    successToastText,
    errorToastText,
    genericErrorToast,
    byErrorCodes,
  } = props;

  // Actual SWR hook.
  const swr = useSWRNext(key, fetcher, config);
  const toastIdSuccess = useRef<string>(`${JSON.stringify(key)}-success`);
  const toastIdError = useRef<string>(`${JSON.stringify(key)}-error`);

  // Static toasts
  const {
    status200Toast, statusGenericErrorToast, showStatusToast,
  } = useStaticToasts();

  // Show success toasts
  // ********************************************
  useEffect(() => {
    if (swr?.data !== undefined) {
      // Toasts
      if (showSuccessToast) {
        if (successToastText) {
          status200Toast(successToastText, toastIdSuccess.current);
        } else {
          status200Toast(undefined, toastIdSuccess.current);
        }
      }
    }
  }, [
    swr?.data,
    status200Toast,
    successToastText,
    showSuccessToast,
  ]);


  // Show error toasts
  // ********************************************
  useEffect(() => {
    if (swr?.error !== undefined) {
      const errorObj = swr?.error as GenericV1ErrorsArray;
      const firstError = errorObj?.errors?.[0];
      const firstCode = firstError?.code;
      const firstMessage = firstError?.message;

      if (showErrorToast) {
        switch (true) {
          case !!errorToastText:
            statusGenericErrorToast(errorToastText, toastIdError.current);
            break;
          case !!genericErrorToast:
            statusGenericErrorToast(undefined, toastIdError.current);
            break;
          case Array.isArray(byErrorCodes) && byErrorCodes.length > 0: {
            const errorText = byErrorCodes.find((item) => item.code === String(firstCode))?.text;
            statusGenericErrorToast(errorText || undefined, toastIdError.current);
            break;
          }
          case firstCode !== undefined: {
            if (firstMessage) {
              showStatusToast(firstCode, { [`message_${firstCode}`]: firstMessage }, undefined, toastIdError.current);
            } else {
              showStatusToast(firstCode, undefined, undefined, toastIdError.current);
            }
            break;
          }
          case firstMessage !== undefined: {
            statusGenericErrorToast(firstMessage, toastIdError.current);
            break;
          }
          default:
            statusGenericErrorToast(undefined, toastIdError.current);
            break;
        }
      }
    }
  }, [
    swr?.error,
    statusGenericErrorToast,
    showStatusToast,
    errorToastText,
    showErrorToast,
    genericErrorToast,
    byErrorCodes,
  ]);


  // Return data
  // ********************************************
  return { ...swr };
};
